import { BarChartOutlined, DashboardOutlined, LoginOutlined, TableOutlined } from '@ant-design/icons'
import { useQuery } from '@apollo/client'
import {
  FetchViewGroupsDocument,
  FetchViewsForSiderDocument,
  type ViewForSiderFieldsFragment,
} from '@salescore/client-api'
import {
  convertFlatViewGroupsToAntdTree,
  deserializeExpandedAntdNode,
  type ExpandedAntdNode,
  useMe,
  useOrganization,
} from '@salescore/client-common'
import { useNavigate, useRouter } from '@tanstack/react-router'
import { Array } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { useMemo } from 'react'

import { type LauncherCommandV2, useMoveCommands } from './useMoveCommands.tsx'

export const useLauncherCommands = ({ pickView }: { pickView: (id: string) => void }): LauncherCommandV2[] => {
  const moveCommands = useMoveCommands()
  const loginCommands = useLoginCommands()
  const moveViewCommands = useMoveViewsCommands({ pickView })
  return [...moveCommands, ...loginCommands, ...moveViewCommands]
}

function useLoginCommands(): LauncherCommandV2[] {
  const router = useRouter()
  const me = useMe()
  const organizationId = me.organization.id
  const { users } = me.myUser.identity
  if (users.length <= 1) {
    return []
  }

  return users
    .filter((user) => user.organization.id !== organizationId)
    .map((user) => ({
      type: `other`,
      icon: <LoginOutlined />,
      title: t(`移動: {{name}}`, { name: user.organization.name }),
      searchKeyword: `move ${user.organization.name.toLowerCase()}`,
      // shortcut: ['command', 'd'],
      method: (): void => {
        // 再現手順は不明だが、組織の移動の際に稀に移動前の組織の情報が残ってしまう不具合があったので
        // 組織移動の時のみredirectではなくlocation.assignを使うように変更
        location.assign(
          router.buildLocation({ to: '/o/$organizationId/v2', params: { organizationId: user.organization.id } })
            .pathname,
          // eslint-disable-next-line sonarjs/todo-tag
        ) // TODO: 常にv2でいいのか？
      },
    }))
}

function useMoveViewsCommands({ pickView }: { pickView: (id: string) => void }): LauncherCommandV2[] {
  const navigate = useNavigate()
  const organization = useOrganization()
  const organizationId = organization.id
  const { data: viewsForSiderData } = useQuery(FetchViewsForSiderDocument, { variables: { organizationId } })
  const { data: viewGroupsData } = useQuery(FetchViewGroupsDocument, { variables: { organizationId } })
  const viewWithGroups = useMemo(() => {
    const views = viewsForSiderData?.viewsForSider ?? []
    const viewGroups = viewGroupsData?.viewGroups ?? []
    const trees = deserializeExpandedAntdNode(convertFlatViewGroupsToAntdTree(viewGroups, views), t)
    const leafs = trees.flatMap((tree) => extractLeaf(tree, []))
    return leafs.map((leaf) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion,sonarjs/todo-tag
      const view = leaf.item as ViewForSiderFieldsFragment // TODO: 雑
      const { type } = view

      const typeLabel = {
        sheet: ``,
        kpi: `KPI`,
        kpiPivot: t(`ダッシュボード`),
        form: t(`フォーム`),
      }[type]
      return {
        title: [typeLabel, view.name].filter(isNotNullish).join(': '),
        subTitle: [
          [
            // eslint-disable-next-line sonarjs/no-nested-conditional
            view.private ? t(`非公開フォルダ`) : view.archived ? t(`ゴミ箱`) : undefined,
            ...leaf.parents.map((parent) => parent.item.name),
          ],
        ]
          .filter(isNotNullish)
          .join(' / '),
        id: view.id,
        type,
        createdBy: view.createdBy ?? undefined,
        archived: view.archived,
      }
    })
  }, [viewGroupsData?.viewGroups, viewsForSiderData?.viewsForSider])
  if (viewWithGroups.length <= 1) {
    return []
  }

  return Array.filter(
    viewWithGroups.map((view): LauncherCommandV2 | undefined => ({
      // eslint-disable-next-line sonarjs/no-nested-conditional
      type: view.archived ? 'archived' : view.type === 'form' ? `sheet` : view.type,
      icon:
        view.type === 'kpiPivot' ? (
          <DashboardOutlined />
        ) : // eslint-disable-next-line sonarjs/no-nested-conditional
        view.type === 'kpi' ? (
          <BarChartOutlined />
        ) : (
          <TableOutlined />
        ),
      title: view.title,
      subTitle: view.subTitle,
      createdBy: view.createdBy,
      searchKeyword: `view ${view.title.toLowerCase()} ${view.subTitle.toLowerCase()} ${view.createdBy?.name ?? ''}`,
      method: (): void => {
        pickView(view.id)
        void navigate({ to: '/o/$organizationId/v2', params: { organizationId }, search: { viewId: view.id } })
      },
    })),
    isNotNullish,
  )
}

function extractLeaf(
  node: ExpandedAntdNode,
  parents: ExpandedAntdNode[],
): Array<ExpandedAntdNode & { parents: ExpandedAntdNode[] }> {
  if (node.isLeaf === true) {
    return [
      {
        ...node,
        parents,
      },
    ]
  }

  return node.children.flatMap((childNode) => extractLeaf(childNode, [...parents, node]))
}
